import type { CatererFormat, EscortCardLayout, PrintPaper, PrintableKind } from '@peles/shared';
import { apiClient } from './client';

export type ExportType =
  | 'expenses'
  | 'timeline'
  | 'checklist'
  | 'guests'
  | 'rsvp-report'
  | 'kibuddim'
  | 'floor-plan'
  | 'seating-chart';
export type ExportFormat = 'pdf' | 'csv';
export type SeatingChartOrder = 'table' | 'name';

export interface SeatingChartExportParams {
  format?: ExportFormat;
  orderBy?: SeatingChartOrder;
  /** Multi-select filters that mirror the guest-list filter bar. */
  side?: string[];
  rsvpStatus?: string[];
  familyGroup?: string[];
}

/**
 * Download a seating chart export. axios serializes array params as
 * `?side=a&side=b`, which the backend's qs parser collapses to
 * `req.query.side = ['a', 'b']`.
 */
export async function downloadSeatingChart(
  eventId: string,
  options: SeatingChartExportParams = {},
): Promise<Blob> {
  const params: Record<string, string | string[]> = {};
  if (options.format === 'csv') params.format = 'csv';
  if (options.orderBy === 'name') params.orderBy = 'name';
  if (options.side?.length) params.side = options.side;
  if (options.rsvpStatus?.length) params.rsvpStatus = options.rsvpStatus;
  if (options.familyGroup?.length) params.familyGroup = options.familyGroup;
  const { data } = await apiClient.get(`/export/events/${eventId}/seating-chart`, {
    params,
    responseType: 'blob',
  });
  return data;
}

export async function downloadExport(
  eventId: string,
  type: ExportType,
  format: ExportFormat = 'pdf',
): Promise<Blob> {
  const params: Record<string, string> = {};
  if (format === 'csv') params.format = 'csv';

  const { data } = await apiClient.get(`/export/events/${eventId}/${type}`, {
    params,
    responseType: 'blob',
  });
  return data;
}

export function triggerDownload(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/** What a printable is asked for with — see `GET /export/events/:eventId/<kind>`. */
export interface PrintableParams {
  paper?: PrintPaper;
  layout?: EscortCardLayout;
  meal?: boolean;
  qr?: boolean;
  /** The invitation whose shared link a table card's QR code opens; else the newest. */
  campaignId?: string;
  format?: CatererFormat;
}

/** Download one of the day's printables (escort, place and table cards, caterer sheet). */
export async function downloadPrintable(
  eventId: string,
  kind: PrintableKind,
  options: PrintableParams = {},
): Promise<Blob> {
  const params: Record<string, string> = {};
  if (options.paper) params.paper = options.paper;
  if (options.layout) params.layout = options.layout;
  if (options.meal) params.meal = '1';
  if (options.qr === false) params.qr = '0';
  if (options.campaignId && options.qr !== false) params.campaignId = options.campaignId;
  if (options.format) params.format = options.format;
  const { data } = await apiClient.get(`/export/events/${eventId}/${kind}`, {
    params,
    responseType: 'blob',
  });
  return data;
}
